type GraphqlError = {
  message: string;
  extensions?: Record<string, unknown>;
};

type GraphqlResponse<TData> = {
  data?: TData;
  errors?: GraphqlError[];
};

export class PokerGraphqlRequestError extends Error {
  public readonly errors: GraphqlError[] | undefined;

  constructor(message: string, errors?: GraphqlError[]) {
    super(message);
    this.name = 'PokerGraphqlRequestError';
    this.errors = errors;
  }
}

const getStoredSessionToken = (): string | null => {
  try {
    return localStorage.getItem('sessionToken');
  } catch {
    return null;
  }
};

const getDefaultGraphqlEndpoint = (): string => {
  try {
    const wsUrl = new URL(import.meta.env.VITE_POKERAPP_SERVER);
    const protocol = wsUrl.protocol === 'wss:' ? 'https:' : 'http:';

    const configuredPortRaw = import.meta.env.VITE_POKERAPP_GRAPHQL_PORT;
    const configuredPort = configuredPortRaw ? Number(configuredPortRaw) : NaN;
    const port = Number.isFinite(configuredPort) ? configuredPort : 4000;

    return `${protocol}//${wsUrl.hostname}:${port}/graphql`;
  } catch {
    return `${window.location.origin}/graphql`;
  }
};

const normalizeGraphqlEndpoint = (raw: string): string => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return trimmed;

  try {
    const url = new URL(trimmed);
    const path = url.pathname || '/';
    const alreadyGraphql = path === '/graphql' || path === '/graphql/';
    if (alreadyGraphql) {
      url.pathname = '/graphql';
      return url.toString();
    }

    if (path.endsWith('/graphql')) {
      return url.toString();
    }

    if (path.endsWith('/')) {
      url.pathname = `${path}graphql`;
      return url.toString();
    }

    url.pathname = `${path}/graphql`;
    return url.toString();
  } catch {
    // Best effort for non-URL inputs
    if (/\/graphql\/?$/.test(trimmed)) return trimmed;
    return trimmed.endsWith('/') ? `${trimmed}graphql` : `${trimmed}/graphql`;
  }
};

export const getPokerGraphqlEndpoint = (): string => {
  const configured = import.meta.env.VITE_POKERAPP_GRAPHQL_URL;
  if (configured && configured.length > 0) return normalizeGraphqlEndpoint(configured);
  return getDefaultGraphqlEndpoint();
};

export async function pokerGraphqlRequest<TData, TVariables extends Record<string, unknown> | undefined = undefined>(args: {
  query: string;
  variables?: TVariables;
  signal?: AbortSignal;
}): Promise<TData> {
  const token = getStoredSessionToken();
  if (!token) {
    throw new PokerGraphqlRequestError('Missing session token');
  }

  const res = await fetch(getPokerGraphqlEndpoint(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      query: args.query,
      variables: args.variables ?? undefined,
    }),
    signal: args.signal,
  });

  let json: GraphqlResponse<TData> | null = null;
  try {
    json = (await res.json()) as GraphqlResponse<TData>;
  } catch {}

  if (!res.ok) {
    const messageFromGraphql = json?.errors?.[0]?.message;
    throw new PokerGraphqlRequestError(messageFromGraphql || `GraphQL request failed (${res.status})`, json?.errors);
  }

  if (json?.errors && json.errors.length > 0) {
    throw new PokerGraphqlRequestError(json.errors[0]?.message || 'GraphQL error', json.errors);
  }

  if (!json?.data) {
    throw new PokerGraphqlRequestError('No data returned from GraphQL');
  }

  return json.data;
}
